import { AttachFile, Save } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Typography,
} from '@mui/material'
import { MuiFileInput } from 'mui-file-input'
import { FunctionComponent, useEffect, useState } from 'react'
import { notDanger, notError, notSuccess } from '../../../../utils/notification'
import { apiArticuloActualizar } from '../../api/apiArticuloActualizar'
import { swalClose, swalException, swalLoading } from '../../../../utils/swal'
import { exportarArticulosCSV } from '../../services/articuloExportar'
import { ArticuloApiInputProps, ArticuloProps } from '../../../../interfaces/articulo.ts'

interface OwnProps {
  id: string
  keepMounted: boolean
  open: boolean
  onClose: (value?: boolean) => void
  data: ArticuloProps[]
}

type Props = OwnProps

const ArticuloActualizarCSVDialog: FunctionComponent<Props> = (props) => {
  const { onClose, open, data, ...other } = props
  const [file, setFile] = useState<File | null>(null)
  const [formLoading, setFormLoading] = useState(false)
  const [progress, setProgress] = useState(0)

  /**
   *
   * @param value Cambio de estado
   */

  const onFileChange = (value: File | null) => {
    if (value) {
      if (value.size > 100000 * 5) {
        notError('Archivo NO superior a 5mb')
      } else {
        setFile(value)
      }
    }
  }
  /**
   * Envio fila por fila para actulizar los articulos
   */
  const [articulosConError, setArticulosConError] = useState<string[]>([])

  const onSubmit = async () => {
    let processed = 0
    //setArticulosConError([])
    if (!file) {
      return
    }
    const reader = new FileReader()
    setFormLoading(true)
    reader.onload = async (event) => {
      const text = event.target?.result as string
      const rows = text.split('\n').filter(Boolean)
      setProgress(0) // <-- Nuevo estado para la barra de progreso
      // Omitir la primera fila (títulos)
      const dataRows = rows.slice(1)
      for (const row of dataRows) {
        // Procesar cada fila
        // Convertir la fila CSV a objeto usando los headers
        const headers = [
          'codigoArticulo',
          'nombreArticulo',
          'descripcionArticulo',
          'codigoActividad',
          'codigoSinProducto',
          'codigoTipoArticulo',
          'articuloCompra',
          'articuloInventario',
          'articuloVenta',
          'complemento',
          'verificarStock',
          'activo',
          'codigoProveedor',
        ]
        const values = row.split(';').map((v) => v.trim())
        const articulo: Record<string, string> = {}
        headers.forEach((header, idx) => {
          articulo[header] = values[idx] || ''
        })

        const { codigoArticulo, ...rest } = articulo
        const input: ArticuloApiInputProps = {
          activo: rest.activo === 'SI' ? true : false,
          articuloCompra:
            rest.articuloCompra === 'SI'
              ? true
              : rest.articuloCompra === 'NO'
                ? false
                : false,
          articuloInventario:
            rest.articuloInventario === 'SI'
              ? true
              : rest.articuloInventario === 'NO'
                ? false
                : false,
          articuloVenta:
            rest.articuloVenta === 'SI'
              ? true
              : rest.articuloVenta === 'NO'
                ? false
                : false,
          claseArticulo: (['PRODUCTO', 'SERVICIO', 'COMPRA'].includes(
            (rest.claseArticulo || '').toUpperCase(),
          )
            ? (rest.claseArticulo || '').toUpperCase()
            : 'PRODUCTO') as 'PRODUCTO' | 'SERVICIO' | 'COMPRA',
          codigoActividad: rest.codigoActividad || '',
          codigoGrupoArticulo: null,
          codigoSinProducto: rest.codigoSinProducto || '',
          codigoTipoArticulo: rest.codigoTipoArticulo || '',
          complemento:
            rest.complemento === 'SI' ? true : rest.complemento === 'NO' ? false : false,
          descripcionArticulo: rest.descripcionArticulo || '',
          codigoProveedor: Array.isArray(rest.codigoProveedor)
            ? rest.codigoProveedor
            : rest.codigoProveedor
              ? rest.codigoProveedor
                  .split(',')
                  .map((v) => v.trim())
                  .filter(Boolean)
              : [],
          gestionArticulo: (['SERIE', 'LOTE'].includes(
            (rest.gestionArticulo || '').toUpperCase(),
          )
            ? (rest.gestionArticulo || '').toUpperCase()
            : null) as 'SERIE' | 'LOTE' | null,
          nombreArticulo: rest.nombreArticulo || '',
          verificarStock:
            rest.verificarStock === 'SI'
              ? true
              : rest.verificarStock === 'NO'
                ? false
                : false,
        }
        const resp = await apiArticuloActualizar(1, codigoArticulo, input).catch((e) => {
          swalException(e)
        })

        await new Promise((res) => setTimeout(res, 500))
        processed++
        setProgress(Math.round((processed / dataRows.length) * 100))
        if (!resp) {
          articulosConError.push(codigoArticulo)
          //notError(`Error al actualizar el artículo: ${codigoArticulo}`)
        }
      }
      setFormLoading(false)
      setProgress(100)
    }
    reader.readAsText(file)
  }
  const exportarDatos = async () => {
    swalLoading()
    try {
      if (!data || data.length === 0) {
        notDanger('No hay datos para exportar.')
        return
      }
      await exportarArticulosCSV(data, true)
    } catch (error: any) {
      console.error(error)
      notDanger(error.message || 'Error al exportar los datos.')
    } finally {
      swalClose()
    }
  }

  useEffect(() => {
    if (progress === 100 && !formLoading) {
      notSuccess('Actualización completada exitosamente')
      setFile(null)
    }
  }, [progress, formLoading])

  const handleClose = () => {
    setFile(null)
    setArticulosConError([])
    onClose()
  }
  return (
    <Dialog maxWidth={'md'} open={open} {...other}>
      <DialogTitle>Actualización Masiva Artículos</DialogTitle>
      <DialogContent dividers>
        <Alert severity={'info'} color={'info'}>
          <Typography>Instrucciones para la importación masiva:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>El archivo debe estar en formato CSV y separado por punto y coma (;).</li>
            <li>No modifique los encabezados de la plantilla.</li>
            <li>
              No cambiar la columna de <b>codigoArtículo</b>, ya que es el identificador
              único.
            </li>
            <li>Los campos booleanos deben tener los valores "SI" o "NO".</li>
            <li>Para varios proveedores, separe los códigos con coma (,).</li>
            <li>Revise que los datos sean correctos antes de importar.</li>
            <li>El tamaño máximo permitido del archivo es 5MB.</li>
          </ul>
        </Alert>
        <Alert severity={'info'} color={'warning'}>
          <Typography>
            Puede descargar la plantilla de importación desde el siguiente enlace.
            <Link component="button" variant="body2" onClick={exportarDatos}>
              Descargar Plantilla CSV
            </Link>
          </Typography>
        </Alert>
        <Box sx={{ width: '100%', mt: 3 }}>
          <MuiFileInput
            fullWidth
            size="small"
            value={file}
            inputProps={{ accept: '.csv' }}
            InputProps={{
              startAdornment: <AttachFile />,
            }}
            placeholder="Seleccionar archivo csv"
            onChange={onFileChange}
          />
        </Box>
        <small>Archivo csv separados por ;</small>
        {formLoading && (
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="body2">Progreso: {progress}%</Typography>
              <Typography variant="body2">
                Artículos restantes:{' '}
                {Math.max(0, data.length - Math.round((progress / 100) * data.length))} /{' '}
                {data.length}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {articulosConError.length > 0 && (
          <Alert severity={'warning'} color={'error'} sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Artículos que no se pudieron actualizar:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {[...new Set(articulosConError)].map((codigo) => (
                <li key={codigo}>{codigo}</li>
              ))}
            </ul>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color={'error'} disabled={formLoading} onClick={handleClose}>
          Cerrar
        </Button>
        <Button
          size={'small'}
          color={'primary'}
          loading={formLoading}
          variant={'contained'}
          sx={{ mr: 2 }}
          startIcon={<Save />}
          onClick={() => {
            onSubmit()
          }}
          disabled={!file}
        >
          Iniciar Importación
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ArticuloActualizarCSVDialog
